import { useState } from "preact/compat"
import { IntegrationWithJobInfo, updateIntegration } from "@/api/integration"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast"

/** Mirrors `db::integration::EarlyPayDiscountMode`. */
export type EarlyPayDiscountMode = "payment_discount" | "credit_memo" | "by_invoice_terms"

const DEFAULT_MODE: EarlyPayDiscountMode = "payment_discount"

interface NetsuiteEarlyPaySettingsProps {
  integration: IntegrationWithJobInfo
}

/**
 * Lets the user say how early-pay (terms) discounts reach NetSuite. The invoice is booked gross, so
 * the discount has to be recognized somewhere for it to clear: either on the payment as
 * "Disc. Taken" (the default — the discount was granted upfront at time of payment, and early-pay
 * invoices then need no GL coding), or coded onto the credit memo as its own line.
 */
export function NetsuiteEarlyPaySettings({ integration }: NetsuiteEarlyPaySettingsProps) {
  const config = integration.data as { early_pay_discount_mode?: EarlyPayDiscountMode | null }
  const [selected, setSelected] = useState<EarlyPayDiscountMode>(config.early_pay_discount_mode ?? DEFAULT_MODE)
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    const res = await updateIntegration(integration.id, { data: { early_pay_discount_mode: selected } })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Save failed", description: "Could not save the early-pay discount setting.", variant: "destructive" })
      return
    }
    toast({ title: "Settings saved", description: "Early-pay discount handling updated." })
  }

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">
        Invoices are booked gross in NetSuite, so an early-pay (terms) discount has to be recognized somewhere for the
        invoice to clear. Choose how yours are handled.
      </p>
      <div class="space-y-1 max-w-md">
        <Label>Early-pay discounts</Label>
        <Select value={selected} onValueChange={(v: string) => setSelected(v as EarlyPayDiscountMode)}>
          <SelectTrigger>
            <SelectValue placeholder="Select handling" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="payment_discount">Given upfront — taken on the payment</SelectItem>
            <SelectItem value="credit_memo">Coded onto the credit memo</SelectItem>
            <SelectItem value="by_invoice_terms">By each invoice's terms</SelectItem>
          </SelectContent>
        </Select>
        <p class="text-xs text-gray-500 pt-1">
          {selected === "payment_discount"
            ? "The discount is sent as each invoice's “Disc. Taken” on the customer payment, and NetSuite posts it to its configured discount account. Early-pay invoices don't need to be coded. Use this only when your invoices' terms carry the discount (for example 2% 10 Net 30): on terms with no discount, such as Net 30, NetSuite does not keep it and the apply is refused."
            : selected === "by_invoice_terms"
              ? "Each invoice's terms in NetSuite decide: terms that carry a discount (for example 2% Net 30) take it on the payment as “Disc. Taken”, and terms with none (such as Net 30) put it on the credit memo, where the early-pay invoice must be coded to exactly one ledger account."
              : "The discount becomes its own credit memo line, so every early-pay invoice must be coded to exactly one ledger account before cash can be applied."}
        </p>
      </div>
      <Button variant="outline" disabled={saving} onClick={handleSave}>
        {saving ? "Saving…" : "Save setting"}
      </Button>
    </div>
  )
}
